import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { createRoot } from "react-dom/client";
import React from "react";
import { BadgeCard } from "./BadgeCard";
import { PAGE_DIMS, perPage, mm, type AveryTemplate, type PrinterCalibration } from "./AveryTemplates";
import type { Attendee, Branding } from "./types";

const PRINT_DPI = 300;

function slug(a: Attendee) {
  const clean = (s: string) => s.replace(/[^A-Za-z0-9]+/g, "_").replace(/^_|_$/g, "");
  return `${clean(a.region || "region")}_${clean(a.registrationCategory)}_${clean(a.fullName || a.registrationId)}`;
}

/**
 * Renders a badge React element into a detached offscreen node and
 * captures it via html2canvas at print DPI. Returns a PNG dataURL.
 */
async function renderBadgeToPng(
  attendee: Attendee,
  branding: Branding | null,
  widthIn: number,
  heightIn: number,
): Promise<string> {
  const host = document.createElement("div");
  host.style.position = "fixed";
  host.style.left = "-10000px";
  host.style.top = "0";
  host.style.pointerEvents = "none";
  document.body.appendChild(host);
  const root = createRoot(host);

  root.render(
    React.createElement(BadgeCard, {
      attendee,
      branding,
      widthIn,
      heightIn,
      dpi: PRINT_DPI,
    }),
  );

  // Poll for the mounted element (React 18 concurrent render is async).
  const mountDeadline = Date.now() + 3000;
  while (!host.firstElementChild && Date.now() < mountDeadline) {
    await new Promise<void>((r) => requestAnimationFrame(() => r()));
  }
  // Extra frame so layout/styles apply
  await new Promise<void>((r) => requestAnimationFrame(() => r()));

  // Wait for images inside the host to load
  const imgs = Array.from(host.querySelectorAll("img"));
  await Promise.all(
    imgs.map(
      (img) =>
        new Promise<void>((res) => {
          if ((img as HTMLImageElement).complete) return res();
          img.addEventListener("load", () => res(), { once: true });
          img.addEventListener("error", () => res(), { once: true });
        }),
    ),
  );

  try {
    const target = host.firstElementChild as HTMLElement;
    if (!target) throw new Error("Badge failed to mount (render timeout)");
    const canvas = await html2canvas(target, {
      scale: 1,
      backgroundColor: "#FFFFFF",
      useCORS: true,
      logging: false,
      width: widthIn * PRINT_DPI,
      height: heightIn * PRINT_DPI,
    });
    return canvas.toDataURL("image/png");
  } finally {
    try { root.unmount(); } catch { /* noop */ }
    host.remove();
  }
}

export async function exportSinglePng(attendee: Attendee, branding: Branding | null, widthIn: number, heightIn: number) {
  const png = await renderBadgeToPng(attendee, branding, widthIn, heightIn);
  const blob = dataURLToBlob(png);
  saveAs(blob, `${slug(attendee)}.png`);
}

export async function exportSinglePdf(
  attendee: Attendee,
  branding: Branding | null,
  widthIn: number,
  heightIn: number,
) {
  const png = await renderBadgeToPng(attendee, branding, widthIn, heightIn);
  const pdf = new jsPDF({
    orientation: widthIn > heightIn ? "landscape" : "portrait",
    unit: "in",
    format: [widthIn, heightIn],
    compress: true,
  });
  pdf.addImage(png, "PNG", 0, 0, widthIn, heightIn, undefined, "FAST");
  pdf.save(`${slug(attendee)}.pdf`);
}

export async function exportZipPng(
  attendees: Attendee[],
  branding: Branding | null,
  widthIn: number,
  heightIn: number,
  onProgress?: (done: number, total: number) => void,
) {
  if (!attendees.length) throw new Error("No attendees selected for export.");
  const zip = new JSZip();
  const failures: { name: string; error: string }[] = [];
  const usedNames = new Set<string>();

  for (let i = 0; i < attendees.length; i++) {
    const a = attendees[i];
    let name = `${slug(a)}.png`;
    let n = 1;
    while (usedNames.has(name)) { name = `${slug(a)}_${++n}.png`; }
    usedNames.add(name);
    try {
      const png = await renderBadgeToPng(a, branding, widthIn, heightIn);
      const b64 = png.split(",")[1] || "";
      if (!b64) throw new Error("Empty PNG output");
      // base64 mode is the most portable across browsers and avoids Blob edge-cases
      zip.file(name, b64, { base64: true });
    } catch (err: any) {
      failures.push({ name: a.fullName || a.registrationId, error: err?.message || String(err) });
      // Continue with the rest so one bad row doesn't kill the whole batch
      // eslint-disable-next-line no-console
      console.error("[badge zip] failed for", a.fullName, err);
    }
    onProgress?.(i + 1, attendees.length);
  }

  if (failures.length === attendees.length) {
    throw new Error(`All ${attendees.length} badges failed to render. First error: ${failures[0]?.error}`);
  }
  if (failures.length) {
    zip.file(
      "_render-errors.txt",
      `Failed to render ${failures.length} of ${attendees.length} badges:\n\n` +
        failures.map((f) => `- ${f.name}: ${f.error}`).join("\n"),
    );
  }

  const blob = await zip.generateAsync(
    { type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } },
    (meta) => onProgress?.(attendees.length, attendees.length),
  );
  saveAs(blob, `ofuesi-badges-${new Date().toISOString().slice(0, 10)}.zip`);
}

/**
 * Bulk PDF: places badges on the selected Avery/custom sheet at exact
 * inch coordinates, one row per page as many as needed.
 */
export async function exportBulkPdf(
  attendees: Attendee[],
  branding: Branding | null,
  template: AveryTemplate,
  onProgress?: (done: number, total: number) => void,
  calibration?: PrinterCalibration,
) {
  const size = PAGE_DIMS[template.pageSize];
  const pageW = template.orientation === "landscape" ? size.h : size.w;
  const pageH = template.orientation === "landscape" ? size.w : size.h;

  const pdf = new jsPDF({
    orientation: template.orientation,
    unit: "in",
    format: [pageW, pageH],
    compress: true,
  });

  const cal = calibration ?? { scaleX: 1, scaleY: 1, offsetX: 0, offsetY: 0, printer: "Default", updatedAt: 0 };
  const offX = mm(cal.offsetX);
  const offY = mm(cal.offsetY);
  const bw = template.badgeW * cal.scaleX;
  const bh = template.badgeH * cal.scaleY;

  const per = perPage(template);
  const total = attendees.length;
  for (let i = 0; i < total; i++) {
    const a = attendees[i];
    const posOnPage = i % per;
    if (i > 0 && posOnPage === 0) pdf.addPage([pageW, pageH], template.orientation);
    const row = Math.floor(posOnPage / template.cols);
    const col = posOnPage % template.cols;
    const x = template.marginLeft + col * (template.badgeW + template.colGap) + offX;
    const y = template.marginTop + row * (template.badgeH + template.rowGap) + offY;
    const png = await renderBadgeToPng(a, branding, bw, bh);
    pdf.addImage(png, "PNG", x, y, bw, bh, undefined, "FAST");
    onProgress?.(i + 1, total);
  }
  pdf.save(`ofuesi-badges-${template.id}-${new Date().toISOString().slice(0, 10)}.pdf`);
}

/** Generates a single calibration/test page: a 60×90 mm rectangle centered
 *  on the sheet, with rulers, center marks and margin guides so the operator
 *  can verify printer accuracy before running a bulk job. */
export async function exportCalibrationPdf(pageSize: "letter" | "a4" = "letter") {
  const size = PAGE_DIMS[pageSize];
  const pdf = new jsPDF({ orientation: "portrait", unit: "in", format: [size.w, size.h], compress: true });
  const cx = size.w / 2, cy = size.h / 2;
  const bw = mm(60), bh = mm(90);
  const x0 = cx - bw / 2, y0 = cy - bh / 2;

  // Central 60×90 mm rectangle
  pdf.setLineWidth(0.01);
  pdf.setDrawColor(20, 30, 60);
  pdf.rect(x0, y0, bw, bh);

  // Center cross
  pdf.setDrawColor(150);
  pdf.line(cx - mm(5), cy, cx + mm(5), cy);
  pdf.line(cx, cy - mm(5), cx, cy + mm(5));

  // Horizontal ruler (top edge) — 10 mm ticks over 100 mm
  pdf.setFontSize(8);
  pdf.setTextColor(60);
  for (let i = -50; i <= 50; i += 10) {
    const xr = cx + mm(i);
    pdf.line(xr, mm(10), xr, mm(10) + mm(i % 50 === 0 ? 4 : 2));
    pdf.text(`${i}`, xr, mm(9), { align: "center" });
  }
  // Vertical ruler (left edge) — 10 mm ticks over 100 mm
  for (let i = -50; i <= 50; i += 10) {
    const yr = cy + mm(i);
    pdf.line(mm(10), yr, mm(10) + mm(i % 50 === 0 ? 4 : 2), yr);
    pdf.text(`${i}`, mm(8), yr + 0.03, { align: "right" });
  }

  // Labels
  pdf.setFontSize(11);
  pdf.setTextColor(20, 30, 60);
  pdf.text("OFUESI Badge Print Calibration · 60 × 90 mm", cx, y0 - mm(10), { align: "center" });
  pdf.setFontSize(9);
  pdf.setTextColor(80);
  pdf.text("Print this page at 100 % scale (no fit-to-page).", cx, y0 - mm(5), { align: "center" });
  pdf.text("Measure the rectangle with a ruler — it must be exactly 60 mm × 90 mm.", cx, y0 + bh + mm(6), { align: "center" });
  pdf.text("If not, open Badge Generator → Calibration and adjust size/offset until it matches.", cx, y0 + bh + mm(11), { align: "center" });

  pdf.save(`ofuesi-badge-calibration-${pageSize}.pdf`);
}

function dataURLToBlob(dataUrl: string): Blob {
  const [meta, b64] = dataUrl.split(",");
  const mime = /data:(.*?);/.exec(meta)?.[1] || "image/png";
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

/**
 * Snapshots the currently-rendered print preview DOM (each `.print-sheet-page`
 * element inside `container`) and stitches them into a multi-page PDF that
 * exactly matches what the user sees on screen. This is the most reliable
 * export path because it reuses the exact DOM the browser already laid out —
 * useful when the offscreen render path fails on a specific machine/browser.
 */
export async function exportPreviewPdf(
  container: HTMLElement,
  pageSize: "letter" | "a4",
  orientation: "portrait" | "landscape",
  onProgress?: (done: number, total: number) => void,
) {
  const pages = Array.from(container.querySelectorAll<HTMLElement>(".print-sheet-page"));
  if (!pages.length) throw new Error("No preview pages to export.");
  const dims = PAGE_DIMS[pageSize];
  const pageW = orientation === "landscape" ? dims.h : dims.w;
  const pageH = orientation === "landscape" ? dims.w : dims.h;
  const pdf = new jsPDF({ orientation, unit: "in", format: [pageW, pageH], compress: true });

  for (let i = 0; i < pages.length; i++) {
    const el = pages[i];
    const prevShadow = el.style.boxShadow;
    el.style.boxShadow = "none";
    const canvas = await html2canvas(el, {
      scale: 2,
      backgroundColor: "#FFFFFF",
      useCORS: true,
      logging: false,
    });
    el.style.boxShadow = prevShadow;
    const png = canvas.toDataURL("image/png");
    if (i > 0) pdf.addPage([pageW, pageH], orientation);
    pdf.addImage(png, "PNG", 0, 0, pageW, pageH, undefined, "FAST");
    onProgress?.(i + 1, pages.length);
  }
  pdf.save(`ofuesi-badges-preview-${new Date().toISOString().slice(0, 10)}.pdf`);
}
